import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, type DayButton } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";

// NOTE: this project is on react-day-picker v9 (see package.json), whose
// classNames keys and component slots are NOT the same as the v8 API most
// older shadcn/ui Calendar snippets online still show (e.g. v8's `day` /
// `cell` / `day_selected` became v9's `day_button` / `day` / `selected` —
// see https://daypicker.dev/v9/upgrading). Selection/today/range styling
// below is applied via the `modifiers` booleans DayPicker passes into a
// custom `DayButton` component instead of relying on classNames selectors
// for those states, since that's the one part of the v9 API that's stable
// regardless of internal DOM structure.
export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  components,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        // `relative` here (not on month_caption — see that key's own
        // comment below) so `nav`'s `absolute inset-x-0 top-0` is
        // contained by THIS box. `months` is nav's actual DOM PARENT in
        // react-day-picker v9 (Root > Months > [Nav, Month(s)] — Nav is a
        // sibling of Month, not nested inside it, confirmed by reading
        // node_modules/react-day-picker/dist/esm/DayPicker.js directly).
        // Without a positioned element here, nav's containing block was
        // whatever positioned ancestor Radix's Popover renders — the
        // whole popover, presets column included — so inset-x-0 spanned
        // the full two-column popover width instead of just the
        // calendar's own width. That's what put the back arrow on top of
        // "Today". It stayed hidden on the plain single-day <DatePicker>
        // only because that popover has no presets column, so "whole
        // popover width" and "this calendar's width" were the same number
        // there.
        months: "relative flex flex-col gap-4 sm:flex-row",
        month: "flex flex-col gap-4",
        // NOT `relative` — that would make this div a positioned element,
        // and since it sits later in the DOM than `nav` (a sibling, not a
        // parent — nav isn't nested inside the caption in this layout) it
        // would then paint (and hit-test) ON TOP of the nav buttons
        // wherever its box overlaps theirs. That's exactly what caused the
        // prev/next arrows to swallow clicks in their center but not their
        // outer edge: this row's box is centered and narrower than the
        // full-width nav row, so only the outer edge of each button poked
        // out from underneath it. Staying non-positioned means it paints
        // behind `nav` (which IS positioned) instead, regardless of DOM
        // order — see CSS's painting-order rules for stacking contexts.
        // h-11 (44px) to match the nav buttons below — they're absolutely
        // positioned so they don't push this row's flow height themselves;
        // if this stayed shorter than the 44px buttons, the buttons would
        // visually spill a few px into the weekday row underneath.
        month_caption: "flex h-11 items-center justify-center",
        caption_label: "text-sm font-medium text-foreground",
        nav: "absolute inset-x-0 top-0 flex items-center justify-between",
        // size-11 (44px), not size-8 (32px) — these are the exact prev/next
        // arrows that were previously hard to click (see the two comments
        // above); undersized touch targets on the one control users
        // specifically complained about is worth fixing along with the
        // positioning bug, not just the positioning.
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "size-11 bg-transparent p-0 opacity-60 hover:bg-accent hover:text-accent-foreground hover:opacity-100"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "size-11 bg-transparent p-0 opacity-60 hover:bg-accent hover:text-accent-foreground hover:opacity-100"
        ),
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday:
          "flex-1 text-center text-xs font-medium uppercase text-muted-foreground",
        week: "mt-1 flex w-full",
        day: "relative flex-1 p-0 text-center text-sm focus-within:relative focus-within:z-20",
        outside: "text-muted-foreground opacity-50",
        disabled: "text-muted-foreground opacity-50",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        // pointer-events-none so a click landing on the glyph itself always
        // bubbles up as a click on the actual <button> (previous/next
        // month) instead of the icon swallowing it — otherwise clicking
        // dead-center on the arrow can silently do nothing while clicking
        // the button's edge still works.
        Chevron: ({ orientation, className: chevronClassName }) => {
          const Icon = orientation === "left" ? ChevronLeft : ChevronRight;
          return <Icon className={cn("size-4 pointer-events-none", chevronClassName)} />;
        },
        DayButton: CalendarDayButton,
        ...components,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      className={cn(
        // size-11 (44px), not size-9 (36px) — CLAUDE.md's own Golden Rule
        // requires a 44x44px minimum touch target on anything interactive;
        // this is a leaf element (fixed px sizing is fine per that same
        // rule), it just needed to actually be 44px.
        "mx-auto size-11 p-0 font-normal transition-colors hover:bg-accent hover:text-accent-foreground",
        modifiers.today &&
          !modifiers.selected &&
          "bg-accent text-accent-foreground font-semibold",
        (modifiers.range_start || modifiers.range_end) &&
          "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground",
        modifiers.range_middle &&
          "rounded-none bg-accent text-accent-foreground hover:bg-accent hover:text-accent-foreground",
        modifiers.selected &&
          !modifiers.range_start &&
          !modifiers.range_end &&
          !modifiers.range_middle &&
          "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground",
        modifiers.outside && "text-muted-foreground opacity-50",
        modifiers.disabled && "text-muted-foreground opacity-50",
        className
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
